'use client'

import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

import { publisherQueryOptions } from '@/api/publisher'
import { TARGET_PAGES } from '@/constants/routes'
import { useQueryParams } from '@/hooks/useQueryParams'
import { sharedWeb } from '@/lib'
import type { LegacyParams } from '@/types/supports'

import { buildBridgeRouteUrl, extractApiParams, resolveTargetPage } from './utils'
import { assertUid, assertUidPrefix, assertValidTarget } from './validation'

interface QueryPageParams extends LegacyParams {
  target: string
}

/**
 * 네이티브에서 전달받은 파라미터를 검증하고 적절한 페이지로 라우팅 합니다.
 *
 * @example
 * before = /bridge?appId=123&uid=user_456&target=support
 * after = /u/{publisher_code}/support?appId=123&uid=user_456&target=support
 */
const BridgeRouter = () => {
  const router = useRouter()
  const { queryParams } = useQueryParams<QueryPageParams>()

  const apiParams = extractApiParams(queryParams)

  const { data, isLoading, error } = useQuery({
    ...publisherQueryOptions.list(apiParams),
    enabled: !!queryParams.appId
  })

  useEffect(() => {
    if (error && (error as any)?.code?.includes('ERR_NETWORK')) {
      sharedWeb.showAlert(
        '잘못된 접근입니다. VPN 혹은 광고 차단 서비스를 사용하고 있다면 해제해 주시길 권장합니다.',
        'SharedWeb.close()'
      )

      return
    }

    if (error) {
      throw error
    }

    if (isLoading || !data) {
      return
    }

    const { publisher } = data
    const uid = queryParams.uid
    const target = queryParams.target

    assertUid(uid, publisher)
    assertUidPrefix(uid, publisher)
    assertValidTarget(target, publisher)

    const resolvedTarget = resolveTargetPage(target, publisher)

    if (resolvedTarget === TARGET_PAGES.SUPPORT || resolvedTarget === TARGET_PAGES.FAQ) {
      const { url, searchParams } = buildBridgeRouteUrl(publisher, resolvedTarget, queryParams)
      const finalUrl = `${url}?${searchParams.toString()}`

      router.replace(finalUrl)
    }
  }, [queryParams, data, isLoading, error, router])

  return null
}

export default BridgeRouter
